/**
 * Type definitions for Notes store
 * 
 * This file provides TypeScript interfaces for note-related data
 * to ensure consistent typing throughout the application.
 */

/**
 * Raw note data as stored in the database
 */
export interface NoteData {
  id: string;
  topic_id: string;
  user_id: string;
  content: string;
  summary: string | null;
  created_at: string;
  updated_at: string;
  metadata?: {
    client_id?: string;
    last_update?: string;
    [key: string]: any;
  };
  user_info?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
}

/**
 * Formatted note for use in the UI
 */
export interface FormattedNote {
  id: string;
  topicId: string;
  userId: string;
  content: string;
  summary: string | null;
  createdAt: string;
  updatedAt: string;
  userInfo?: {
    name: string | null;
    email: string | null;
    avatarUrl: string | null;
  };
}

/**
 * Input for creating a new note
 */
export interface CreateNoteInput {
  topicId: string;
  content: string;
  summary?: string | null;
}

/**
 * Input for updating an existing note
 */
export interface UpdateNoteInput {
  content: string;
  summary?: string | null;
}

/**
 * Note filter options
 */
export interface NoteFilters {
  searchQuery?: string;
  userIds?: string[];
  // TODO: Enhance filtering capabilities to match Task implementation
  // Consider adding support for:
  // - Date range filtering
  // - Content type filtering
  // - Sorting options
}

/**
 * Helper function to format a note for consumption in UI components
 */
export function formatNote(note: NoteData): FormattedNote {
  return {
    id: note.id,
    topicId: note.topic_id,
    userId: note.user_id,
    content: note.content,
    summary: note.summary,
    createdAt: note.created_at,
    updatedAt: note.updated_at,
    userInfo: note.user_info ? {
      name: note.user_info.name || null,
      email: note.user_info.email || null,
      avatarUrl: note.user_info.avatar_url || null
    } : undefined
  };
}

/**
 * Helper function to format a note for sending to the database
 */
export function formatNoteForDatabase(note: FormattedNote): NoteData {
  return {
    id: note.id,
    topic_id: note.topicId,
    user_id: note.userId,
    content: note.content,
    summary: note.summary,
    created_at: note.createdAt,
    updated_at: note.updatedAt,
    user_info: note.userInfo ? {
      name: note.userInfo.name || undefined,
      email: note.userInfo.email || undefined,
      avatar_url: note.userInfo.avatarUrl || undefined
    } : undefined
  };
}